const sliderInstances = new Map();

class ProductGallerySliderController {
  constructor(galleryContainer) {
    this.galleryContainer = galleryContainer;
    this.mainGallerySelector = "[main-gallery-swiper]";
    this.thumbGallerySelector = "[thumb-gallery-swiper]";
    this.resizeTimer = null;
    this.resizeListener = null;

    this.startInitialisation();
  }

  startInitialisation() {
    if (!this.galleryContainer) {
      return;
    }

    if (window.customElements && typeof window.customElements.whenDefined === "function") {
      if (window.customElements.get("swiper-container")) {
        this.init();
      } else { 
        window.customElements
          .whenDefined("swiper-container")
          .then(() => {
            this.init();
          })
          .catch((error) => {
            console.warn(
              "ProductGallerySliderController: Failed to wait for swiper-container",
              error,
            );
            this.markGalleryReady();
          });
      }
    } else {
      this.init();
    }
  }

  init() {
    if (!this.galleryContainer?.isConnected) {
      return;
    }

    this.main_gallery = this.galleryContainer.querySelector(this.mainGallerySelector);
    this.thumb_gallery = this.galleryContainer.querySelector(this.thumbGallerySelector);
    this.thumb_positioning = this.galleryContainer.getAttribute("data-thumb-positioning") || "left";

    if (!this.thumb_gallery) {
      const alternativeSelectors = [
        "swiper-container[data-thumb]",
        "swiper-container.thumb-swiper",
        ".thumb-gallery swiper-container",
        "[data-thumb-swiper]",
        "#product-thumb-swiper",
        "swiper-container:nth-child(2)",
      ];

      for (const selector of alternativeSelectors) {
        this.thumb_gallery = this.galleryContainer.querySelector(selector);
        if (this.thumb_gallery) {
          break;
        }
      }
    }

    this.setupResizeListener();
    this.initGallery();
  }

  syncThumbHeight() {
    if (this.thumb_positioning === "under") {
      return;
    }

    if (!this.main_gallery || !this.thumb_gallery) {
      return;
    }

    const mainHeight = this.main_gallery.getBoundingClientRect().height;

    if (mainHeight > 0) {
      const thumbGallery = this.thumb_gallery.closest(".thumb-gallery");

      if (thumbGallery) {
        thumbGallery.style.height = `${mainHeight}px`;
        thumbGallery.style.maxHeight = `${mainHeight}px`;
        thumbGallery.style.minHeight = `${mainHeight}px`;
        thumbGallery.style.overflow = "hidden";

        this.thumb_gallery.style.height = "100%";
        this.thumb_gallery.style.maxHeight = "100%";
        this.thumb_gallery.style.overflow = "hidden";

        this.injectThumbSwiperStyles(mainHeight);

        if (this.thumb_gallery.swiper && typeof this.thumb_gallery.swiper.update === "function") {
          this.thumb_gallery.swiper.update();
        }
      }
    }
  }

  injectThumbSwiperStyles(mainHeight) {
    if (!this.thumb_gallery.id) {
      return;
    }

    const styleId = `thumb-swiper-height-fix-${this.thumb_gallery.id}`;
    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
      #${this.thumb_gallery.id} {
        height: ${mainHeight}px !important;
        max-height: ${mainHeight}px !important;
      }
      #${this.thumb_gallery.id}::part(wrapper) {
        height: 100% !important;
        max-height: 100% !important;
        flex-direction: column !important;
      }
    `;

    const existingStyle = document.getElementById(styleId);
    if (existingStyle) {
      existingStyle.remove();
    }
    document.head.appendChild(style);
  }

  setupResizeListener() {
    if (this.resizeListener || typeof window === "undefined") {
      return;
    }

    this.resizeListener = () => {
      if (this.resizeTimer) {
        window.clearTimeout(this.resizeTimer);
      }
      this.resizeTimer = window.setTimeout(() => {
        this.resizeTimer = null;
        this.syncThumbHeight();
      }, 250);
    };

    window.addEventListener("resize", this.resizeListener, { passive: true });
  }

  markGalleryReady() {
    if (this.galleryContainer) {
      this.galleryContainer.classList.remove("opacity-0");
      this.galleryContainer.classList.add("opacity-1");
    }
  }

  initialiseSwiperElement(el) {
    if (!el) {
      return Promise.resolve(null);
    }

    if (el.swiper) {
      return Promise.resolve(el.swiper);
    }

    if (typeof el.initialize === "function") {
      try {
        const initResult = el.initialize();
        if (initResult && typeof initResult.then === "function") {
          return initResult.then(() => {
            return el.swiper || null;
          });
        }
        return Promise.resolve(el.swiper || null);
      } catch (error) {
        console.error(
          "ProductGallerySliderController: Error during element initialization:",
          error,
        );
        return Promise.reject(error);
      }
    }

    return Promise.resolve(null);
  }

  initGallery() {
    const thumbPromise = this.initialiseSwiperElement(this.thumb_gallery);
    thumbPromise
      .then(() => {
        return this.initialiseSwiperElement(this.main_gallery);
      })
      .then((mainSwiperInstance) => {
        if (mainSwiperInstance) {
          window.gallery_swiper = mainSwiperInstance;
        }
        if (this.thumb_gallery && this.thumb_gallery.swiper) {
          window.gallery_thumb_swiper = this.thumb_gallery.swiper;
        }

        this.markGalleryReady();

        window.setTimeout(() => {
          this.syncThumbHeight();
        }, 100);
      })
      .catch((error) => {
        console.error("ProductGallerySliderController: Failed to initialise Swiper:", error);
        this.markGalleryReady();
      });
  }

  destroy() {
    if (this.resizeTimer && typeof window !== "undefined") {
      window.clearTimeout(this.resizeTimer);
      this.resizeTimer = null;
    }

    if (this.resizeListener && typeof window !== "undefined") {
      window.removeEventListener("resize", this.resizeListener);
      this.resizeListener = null;
    }

    if (this.thumb_gallery?.id) {
      const styleId = `thumb-swiper-height-fix-${this.thumb_gallery.id}`;
      const style = document.getElementById(styleId);
      if (style) {
        style.remove();
      }
    }
  }
}

function findGalleryContainers(root = document) {
  if (!root) {
    return [];
  }

  if (root.matches?.("[gallery-container]")) {
    return [root];
  }

  if (typeof root.querySelectorAll !== "function") {
    return [];
  }

  return Array.from(root.querySelectorAll("[gallery-container]"));
}

function initGallerySliders(root = document) {
  const containers = findGalleryContainers(root);
  containers.forEach((container) => {
    if (!container || sliderInstances.has(container)) {
      return;
    }
    const instance = new ProductGallerySliderController(container);
    sliderInstances.set(container, instance);
  });
}

function teardownGallerySliders(root = document) {
  const containers = findGalleryContainers(root);
  containers.forEach((container) => {
    const instance = sliderInstances.get(container);
    if (!instance) {
      return;
    }
    instance.destroy();
    sliderInstances.delete(container);
  });
}

function onDomReady(callback) {
  if (typeof document === "undefined") {
    return;
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", callback, { once: true });
  } else {
    callback();
  }
}

onDomReady(() => initGallerySliders(document));

if (typeof document !== "undefined") {
  document.addEventListener("shopify:section:load", (event) => {
    initGallerySliders(event?.target || document);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    teardownGallerySliders(event?.target || document);
  });
}

if (typeof window !== "undefined") {
  window.QF = window.QF || {};
  window.QF.productGallerySlider = {
    init: initGallerySliders,
    destroy: teardownGallerySliders,
    instances: sliderInstances,
  };
}
